import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client.safe';
import { Database, RefreshCw, AlertTriangle, CheckCircle, Clock, Trash2 } from 'lucide-react';
import { useDevMode } from '@/hooks/useDevMode';
import { Navigate } from 'react-router-dom';

interface HealthCheck {
  name: string;
  status: 'ok' | 'warning' | 'error';
  count: number;
  description: string;
}

const AdminDatabaseHealthPage: React.FC = () => {
  const { effectiveRole, isSimulating, loading: roleLoading } = useDevMode();
  const [healthChecks, setHealthChecks] = useState<HealthCheck[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Only system_admin can access this page (and not when simulating)
  // Wait for role to load before checking access
  const hasAccess = !roleLoading && effectiveRole === 'system_admin' && !isSimulating;

  const runHealthChecks = async () => {
    setLoading(true);
    const checks: HealthCheck[] = [];

    try {
      // Check 1: Orphan findings (findings without valid revision)
      const { count: orphanFindings } = await supabase
        .from('audit_findings')
        .select('*', { count: 'exact', head: true })
        .is('run_id', null);

      checks.push({
        name: 'Orphan Findings',
        status: (orphanFindings || 0) > 0 ? 'warning' : 'ok',
        count: orphanFindings || 0,
        description: 'Findings utan koppling till revision'
      });

      // Check 2: Stuck revisions (in_progress > 1 hour)
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
      const { count: stuckRevisions } = await supabase
        .from('revisions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'in_progress')
        .lt('updated_at', oneHourAgo);

      checks.push({
        name: 'Stuck Revisions',
        status: (stuckRevisions || 0) > 0 ? 'warning' : 'ok',
        count: stuckRevisions || 0,
        description: 'Revisioner som fastnat i in_progress > 1h'
      });

      // Check 3: Stuck batches
      const { count: stuckBatches } = await supabase
        .from('batches')
        .select('*', { count: 'exact', head: true })
        .eq('state', 'in_progress')
        .lt('updated_at', oneHourAgo);

      checks.push({
        name: 'Stuck Batches',
        status: (stuckBatches || 0) > 0 ? 'warning' : 'ok',
        count: stuckBatches || 0,
        description: 'Batches som fastnat i in_progress > 1h'
      });

      // Check 4: Failed batches
      const { count: failedBatches } = await supabase
        .from('batches')
        .select('*', { count: 'exact', head: true })
        .eq('state', 'failed');

      checks.push({
        name: 'Failed Batches',
        status: (failedBatches || 0) > 5 ? 'error' : (failedBatches || 0) > 0 ? 'warning' : 'ok',
        count: failedBatches || 0,
        description: 'Batches med status failed'
      });

      // Check 5: Pending queue items
      const { count: pendingQueue } = await supabase
        .from('worker_queue')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      checks.push({
        name: 'Pending Queue',
        status: (pendingQueue || 0) > 100 ? 'warning' : 'ok',
        count: pendingQueue || 0,
        description: 'Väntande jobb i worker_queue'
      });

      // Check 6: Total revisions
      const { count: totalRevisions } = await supabase
        .from('revisions')
        .select('*', { count: 'exact', head: true });

      checks.push({
        name: 'Total Revisions',
        status: 'ok',
        count: totalRevisions || 0,
        description: 'Totalt antal revisioner'
      });

      // Check 7: Completed revisions today
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const { count: completedToday } = await supabase
        .from('revisions')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'completed')
        .gte('completed_at', todayStart.toISOString());

      checks.push({
        name: 'Completed Today',
        status: 'ok',
        count: completedToday || 0,
        description: 'Slutförda revisioner idag'
      });

    } catch (error) {
      console.error('Health check error:', error);
      checks.push({
        name: 'Error',
        status: 'error',
        count: 0,
        description: 'Kunde inte köra health checks'
      });
    }

    setHealthChecks(checks);
    setLastRefresh(new Date());
    setLoading(false);
  };

  useEffect(() => {
    if (hasAccess) {
      runHealthChecks();
    }
  }, [hasAccess]);

  // Show loading while role is being determined
  if (roleLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }

  const getStatusIcon = (status: HealthCheck['status']) => {
    switch (status) {
      case 'ok':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'error':
        return <Trash2 className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusBadge = (status: HealthCheck['status']) => {
    switch (status) {
      case 'ok':
        return <Badge className="bg-green-100 text-green-800 border-green-200">OK</Badge>;
      case 'warning':
        return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Varning</Badge>;
      case 'error':
        return <Badge className="bg-red-100 text-red-800 border-red-200">Fel</Badge>;
    }
  };

  const hasIssues = healthChecks.some(c => c.status !== 'ok');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Database Health</h1>
            <p className="text-muted-foreground">Diagnostik och hälsokontroller</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {lastRefresh && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Uppdaterad: {lastRefresh.toLocaleTimeString('sv-SE')}
            </div>
          )}
          <Button onClick={runHealthChecks} disabled={loading} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Uppdatera
          </Button>
        </div>
      </div>

      {/* Summary */}
      <Card className={hasIssues ? 'border-amber-200 bg-amber-50/50' : 'border-green-200 bg-green-50/50'}>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            {hasIssues ? (
              <>
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Vissa problem upptäckta
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 text-green-500" />
                Allt ser bra ut
              </>
            )}
          </CardTitle>
          <CardDescription>
            {healthChecks.filter(c => c.status !== 'ok').length} av {healthChecks.length} kontroller kräver uppmärksamhet
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Health Checks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {healthChecks.map((check, index) => (
          <Card key={index} className={
            check.status === 'error' ? 'border-red-200' :
            check.status === 'warning' ? 'border-amber-200' :
            'border-green-200'
          }>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  {getStatusIcon(check.status)}
                  {check.name}
                </CardTitle>
                {getStatusBadge(check.status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{check.count.toLocaleString()}</div>
              <p className="text-sm text-muted-foreground mt-1">{check.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
};

export default AdminDatabaseHealthPage;
